/**
 * Renders a tree view of sites and groups.
 * @param {Object} props - The component props.
 * @param {Array} props.siteAndGroups - The array of site and group data.
 * @param {Function} props.onSitesAndGroupSearch - The callback function for searching sites and groups.
 * @returns {JSX.Element} The rendered component.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Grow,
  Collapse,
  Fade,
  Checkbox,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { StyledIconButton } from "../../../../components/StyledComponents/Buttons/AuradineButtons.js";
import {
  BodyLarge,
  BodySmall,
  LabelSmall,
} from "../../../../components/StyledComponents/Typography/Typography.tsx";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import Close from "@mui/icons-material/Close";
import { StyledCheckbox } from "../../../../components/StyledComponents/Inputs/Inputs.js";
import { check } from "prettier";
import useMinerPageContext from "../../../context/useMinerPageContext.js";
import useSiteAndGroupTreeContext from "../../../context/useSiteAndGroupTreeContext.js";
import { useLocation } from "react-router-dom/cjs/react-router-dom.js";
import { useHistory } from "react-router-dom";
import { useTheme } from "../../../../ThemeContext/ThemeContext.js";

/**The SiteAndGroupTreeItem component takes in several props:
site: Represents the current site.
setSiteSelected: A function to update the selected site.
siteSelected: Represents the currently selected site.
setSiteSelectedName: A function to update the name of the selected site.
setGroupSelectedName: A function to update the name of the selected group.
siteSelectedName: Represents the name of the currently selected site.
groupSelectedName: Represents the name of the currently selected group. */

//Inside the component, there is a state variable called treeOpen initialized with a value of false. This state variable will be used to control the visibility of the tree.
const SiteAndGroupTreeItem = (props) => {
  const { colors } = useTheme();
  const {
    site,
    expand,
    setSiteSelected,
    siteSelected,
    setSiteSelectedName,
    setGroupSelectedName,
    siteSelectedName,
    groupSelectedName,
    clickDisabled,
    multiSelectModel,
    setSiteAndGroups,
    siteAndGroups,
    setNumberOfSelections,
    numberOfSelections,
    sidePanelMode,
  } = props;
  const [treeOpen, setTreeOpen] = useState(false);
  let allGroupSelectedInSite = false;
  const location = useLocation();
  const history = useHistory();

  const onSiteSelectedMatriceChange = (e) => {
    if (e.target.checked) {
      const siteCount = 1;
      const groupCount = site.groups.reduce((acc, curr) => {
        return acc + 1;
      }, 0);
      if (
        site.groups.some((group) => group.selectedGroup)
      ) {
        const groupCount = site.groups.reduce(
          (acc, curr) => {
            return !curr.selectedGroup ? acc + 1 : acc;
          },
          0
        );
        setNumberOfSelections(
          (prev) => prev + siteCount + groupCount
        );
      } else {
        setNumberOfSelections(
          (prev) => prev + siteCount + groupCount
        );
      }
      const siteModifications = {
        ...site,
        selectedSite: e.target.checked,
        groups: site.groups.map((group) => ({
          ...group,
          selectedGroup: true,
        })),
      };
      const siteGroupInfo = [...siteAndGroups];
      const indexToReplaceAt = siteGroupInfo.findIndex(
        (siteGroup) => site.siteName === siteGroup.siteName
      );
      siteGroupInfo.splice(
        indexToReplaceAt,
        1,
        siteModifications
      );
      setSiteAndGroups(siteGroupInfo);
    } else {
      const siteModifications = {
        ...site,
        selectedSite: e.target.checked,
        groups: site.groups.map((group) => ({
          ...group,
          selectedGroup: false,
        })),
      };
      const siteCount = 1;
      const groupCount = site.groups.reduce((acc, curr) => {
        return acc + 1;
      }, 0);
      setNumberOfSelections((prev) =>
        prev - siteCount - groupCount <= 0
          ? 0
          : prev - siteCount - groupCount
      );
      const siteGroupInfo = [...siteAndGroups];
      const indexToReplaceAt = siteGroupInfo.findIndex(
        (siteGroup) => site.siteName === siteGroup.siteName
      );
      siteGroupInfo.splice(
        indexToReplaceAt,
        1,
        siteModifications
      );
      setSiteAndGroups(siteGroupInfo);
    }
  };

  const checkAllGroupsSelected = () => {
    return (
      site.groups.length > 0 &&
      site.groups?.every((group) => group.selectedGroup)
    );
  };

  const checkAllGroupsDeselected = () => {
    return (
      site.groups.length > 0 &&
      site.groups?.every((group) => !group.selectedGroup)
    );
  };

  const onGroupSelectedMatriceChange = (e, index) => {
    if (e.target.checked) {
      console.log("in here");
      const siteModifications = {
        ...site,
        groups: site.groups.map((group, i) =>
          i === index
            ? { ...group, selectedGroup: e.target.checked }
            : group
        ),
      };

      if (
        siteModifications.groups.every(
          (group) => group.selectedGroup
        )
      ) {
        console.log("");
        siteModifications.selectedSite = true;
        setNumberOfSelections((prev) => prev + 2);
      } else {
        setNumberOfSelections((prev) => prev + 1);
      }

      const siteGroupInfo = [...siteAndGroups];
      const indexToReplaceAt = siteGroupInfo.findIndex(
        (siteGroup) => site.siteName === siteGroup.siteName
      );
      siteGroupInfo.splice(
        indexToReplaceAt,
        1,
        siteModifications
      );
      setSiteAndGroups(siteGroupInfo);
    } else {
      const siteModifications = {
        ...site,
        groups: site.groups.map((group, i) =>
          i === index
            ? { ...group, selectedGroup: e.target.checked }
            : group
        ),
      };

      if (
        siteModifications.groups.some(
          (group) =>
            !group.selectedGroup &&
            siteModifications.selectedSite
        )
      ) {
        siteModifications.selectedSite = false;
        setNumberOfSelections((prev) => prev - 2);
      } else {
        setNumberOfSelections((prev) => prev - 1);
      }
      const siteGroupInfo = [...siteAndGroups];
      const indexToReplaceAt = siteGroupInfo.findIndex(
        (siteGroup) => site.siteName === siteGroup.siteName
      );
      siteGroupInfo.splice(
        indexToReplaceAt,
        1,
        siteModifications
      );
      setSiteAndGroups(siteGroupInfo);
    }
  };

  const checkAtleastOneGroupSelected = () => {
    return (
      site.groups.length > 0 &&
      site.groups?.some((group) => group.selectedGroup)
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(
      window.location.search
    );
    let siteName = params.get("siteName");
    let dgname = params.get("dgname");

    if (!siteName && !dgname) {
      const siteGroup =
        JSON.parse(sessionStorage.getItem("siteGroup")) ||
        {};
      siteName = siteGroup.siteName;
      dgname = siteGroup.dgname;
    }

    if (siteName) {
      setSiteSelectedName(siteName);
      if (!dgname) setSiteSelected(true);
    }

    if (dgname) {
      setGroupSelectedName(dgname);
      setSiteSelected(false);
    }
  }, []);

  useEffect(() => {
    if (expand && !clickDisabled) {
      setTreeOpen(true);
    }
  }, [expand, clickDisabled]);

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      borderBottom={`1px solid ${colors.border.card}`}
    >
      <Box
        className={"site_information"}
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        padding={" 0.5rem 1.5rem 0.5rem 1rem"}
        onClick={() => {
          if (!clickDisabled) {
            console.log("site clicked");
            const url = new URL(window.location.href);
            url.searchParams.set(
              "siteName",
              site?.siteName
            );
            url.searchParams.delete("dgname");
            window.history.replaceState({}, "", url);
            setSiteSelected(true);
            setSiteSelectedName(site?.siteName);
            setGroupSelectedName("");
            const siteGroup = {
              siteName: site?.siteName,
              dgname: "",
            };
            sessionStorage.setItem(
              "siteGroup",
              JSON.stringify(siteGroup)
            );
          }
        }}
        gap={{ xs: 1, sm: 2, md: 4 }}
        sx={{
          cursor: "pointer",
          transition: "all 0.3s ease-in-out",
          backgroundColor:
            !clickDisabled &&
            siteSelectedName === site?.siteName &&
            siteSelected
              ? colors.primary.light 
              : "",
          borderRadius: "6.25rem",
          "&:hover": {
            backgroundColor:
              !clickDisabled
                ? colors.hover.muted
                  : colors.hover.muted,
            borderRadius: "6.25rem",
          },
        }}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
          flexGrow={1}
          alignItems={"center"}
        >
          {!clickDisabled &&
            siteSelectedName === site?.siteName &&
            siteSelected && (
              <StyledIconButton colors={colors}
                onClick={(event) => {
                  event.stopPropagation();
                  setGroupSelectedName("");
                  setSiteSelectedName("");
                  setSiteSelected(false);
                  const url = new URL(window.location.href);
                  url.searchParams.delete("siteName");
                  url.searchParams.delete("dgname");
                  window.history.replaceState({}, "", url);
                  const siteGroup = {
                    siteName: "",
                    dgname: "",
                  };
                  sessionStorage.setItem(
                    "siteGroup",
                    JSON.stringify(siteGroup)
                  );
                }}
              >
                <Close
                  fontSize="small"
                />
              </StyledIconButton>
            )}
          <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            flexShrink={1}
            gap={2}
          >
            {multiSelectModel && (
              <StyledCheckbox
                colors={colors}
                indeterminate={
                  checkAtleastOneGroupSelected() &&
                  !checkAllGroupsSelected()
                }
                checked={
                  site.selectedSite ||
                  checkAllGroupsSelected()
                }
                onChange={onSiteSelectedMatriceChange}
              />
            )}
            <Tooltip title={site?.siteLocation ? `${site?.siteName} - ${site?.siteLocation}` : site?.siteName}>
              <Box>
                <BodyLarge
                  noWrap
                  sx={
                    sidePanelMode
                      ? {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: {
                            xs: "3rem",
                            sm: "4rem",
                            md: "8rem",
                            lg: "10rem",
                            xl: "12rem",
                          },
                        }
                      : {}
                  }
                >
                  {site?.siteName}
                </BodyLarge>
                {site?.siteLocation && (
                  <BodySmall
                    noWrap
                    sx={{
                      color: colors.text.muted,
                      fontSize: "0.75rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: {
                        xs: "3rem",
                        sm: "4rem",
                        md: "8rem",
                        lg: "10rem",
                        xl: "12rem",
                      },
                    }}
                  >
                    {site?.siteLocation}
                  </BodySmall>
                )}
              </Box>
            </Tooltip>
          </Box>
          <LabelSmall> {site?.deviceCount} </LabelSmall>
        </Box>
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"flex-end"}
          alignItems={"center"}
        >
          <StyledIconButton colors={colors}
            onClick={(event) => {
              event.stopPropagation();
              setTreeOpen((prev) => !prev);
            }}
            disabled={
              site?.groups && site?.groups?.length === 0
            }
          >
            {treeOpen ? (
              <>
                <ArrowDropUpIcon />
              </>
            ) : (
              <>
                <ArrowDropDownIcon />
              </>
            )}
          </StyledIconButton>
        </Box>
      </Box>
      {treeOpen &&
        site?.groups &&
        site?.groups?.length > 0 &&
        site.groups.map((groups, index) => {
          return (
            <Fade in={treeOpen} key={index}>
              <Box
                className={"group_information"}
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                padding={" 0.5rem 1.5rem 0.5rem 1rem"}
                gap={{ xs: 1, sm: 2, md: 4 }}
                onClick={() => {
                  if (!clickDisabled) {
                    setSiteSelected(false);
                    setGroupSelectedName(groups?.DGName);
                    setSiteSelectedName(site?.siteName);
                    const url = new URL(
                      window.location.href
                    );
                    url.searchParams.set(
                      "siteName",
                      site?.siteName
                    );
                    url.searchParams.set(
                      "dgname",
                      groups?.DGName
                    );
                    window.history.replaceState(
                      {},
                      "",
                      url
                    );
                    const siteGroup = {
                      siteName: site?.siteName,
                      dgname: groups?.DGName,
                    };
                    sessionStorage.setItem(
                      "siteGroup",
                      JSON.stringify(siteGroup)
                    );
                  }
                }}
                sx={{
                  cursor: "pointer",
                  transition: "all 0.3s ease-in-out",
                  borderRadius: "6.25rem",
                  backgroundColor:
                    groupSelectedName === groups?.DGName &&
                    !clickDisabled &&
                    siteSelectedName === site?.siteName &&
                    !siteSelected
                      ? colors.primary.light  
                      : "",
                  "&:hover": {
                    backgroundColor:
                      !clickDisabled 
                        ? colors.hover.muted
                        : colors.hover.muted,
                    borderRadius: "6.25rem",
                    // padding: "1rem 1.5rem",
                  },
                }}
              >
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"space-between"}
                  flexGrow={1}
                  alignItems={"center"}
                >
                  {groupSelectedName === groups?.DGName &&
                    !clickDisabled &&
                    siteSelectedName === site?.siteName &&
                    !siteSelected && (
                      <StyledIconButton colors={colors}
                        onClick={(event) => {
                          event.stopPropagation();
                          setGroupSelectedName("");
                          setSiteSelectedName("");
                          setSiteSelected(false);
                          const url = new URL(
                            window.location.href
                          );
                          url.searchParams.delete(
                            "siteName"
                          );
                          url.searchParams.delete("dgname");
                          window.history.replaceState(
                            {},
                            "",
                            url
                          );
                          const siteGroup = {
                            siteName: "",
                            dgname: "",
                          };
                          sessionStorage.setItem(
                            "siteGroup",
                            JSON.stringify(siteGroup)
                          );
                        }}
                      >
                        <Close
                          sx={{ color: colors.text.muted }}
                          fontSize="small"
                        />
                      </StyledIconButton>
                    )}
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    alignItems={"center"}
                    gap={2}
                  >
                    <Box
                      width={multiSelectModel ? 50 : 10}
                    ></Box>

                    {multiSelectModel && (
                      <StyledCheckbox
                        colors={colors}
                        checked={
                          site.groups[index].selectedGroup
                        }
                        onChange={(e) =>
                          onGroupSelectedMatriceChange(
                            e,
                            index
                          )
                        }
                      />
                    )}
                    <Tooltip title={groups?.DGName}>
                      <Box>
                        <BodyLarge
                          noWrap
                          sx={
                            sidePanelMode
                              ? {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: {
                                    xs: "3rem",
                                    sm: "4rem",
                                    md: "8rem",
                                    lg: "10rem",
                                    xl: "12rem",
                                  },
                                }
                              : {}
                          }
                        >
                          {groups?.DGName}
                        </BodyLarge>
                      </Box>
                    </Tooltip>
                  </Box>
                  <LabelSmall>
                    {" "}
                    {groups?.deviceCount}{" "}
                  </LabelSmall>
                </Box>
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  gap={2.48}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                >
                  <Box
                    width={"2.125rem"}
                    height={"2.125rem"}
                  ></Box>
                </Box>
              </Box>
            </Fade>
          );
        })}
    </Box>
  );
};

//A  group tree is a hierarchical representation of sites and groups from our groups and sites management system.
const SiteAndGroupTree = (props) => {
  const {
    siteAndGroups,
    setSiteAndGroups,
    onSitesAndGroupSearch,
    clickDisabled,
    multiSelectModel,
    maxHeight,
    setNumberOfSelections,
    numberOfSelections,
    isSiteGroupDataFetching,
    isSiteGroupDataLoading,
    isSiteGroupDataFetched,
    sidePanelMode,
    searchValue,
  } = props;
  const [treeOpen, setTreeOpen] = React.useState(false);
  const [siteSelected, setSiteSelected] = useState(false);
  const {
    setSiteSelectedName,
    setGroupSelectedName,
    siteSelectedName,
    groupSelectedName,
  } = useSiteAndGroupTreeContext();
  const { colors } = useTheme();
  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      flexGrow={1}
      overflow={"auto"}
      maxHeight={
        maxHeight ? maxHeight : "calc(100vh - 23rem)"
      }
    >
      {isSiteGroupDataFetched &&
        siteAndGroups &&
        siteAndGroups.length > 0 &&
        siteAndGroups?.map((site, index) => (
          <SiteAndGroupTreeItem
            key={index}
            site={site}
            treeOpen={treeOpen}
            setTreeOpen={setTreeOpen}
            expand={
              site.siteName === siteSelectedName &&
              !!groupSelectedName
            }
            siteSelected={siteSelected}
            setSiteSelected={setSiteSelected}
            siteSelectedName={siteSelectedName}
            setSiteSelectedName={setSiteSelectedName}
            groupSelectedName={groupSelectedName}
            setGroupSelectedName={setGroupSelectedName}
            clickDisabled={clickDisabled}
            multiSelectModel={multiSelectModel}
            setSiteAndGroups={setSiteAndGroups}
            siteAndGroups={siteAndGroups}
            setNumberOfSelections={setNumberOfSelections}
            numberOfSelections={numberOfSelections}
            sidePanelMode={props.sidePanelMode}
          />
        ))}

      {}

      {isSiteGroupDataLoading && (
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"center"}
          alignItems={"center"}
          flexGrow={1}
        >
          {" "}
          <CircularProgress
            size={"2rem"}
            sx={{
              color: colors.primary.main,
            }}
          />
        </Box>
      )}

      {/* push this out of siteGroup */}
      {isSiteGroupDataFetched &&
        siteAndGroups &&
        siteAndGroups.length === 0 &&
        searchValue !== "" && (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            flexGrow={1}
          >
            <BodyLarge>No search results found</BodyLarge>
          </Box>
        )}
    </Box>
  );
};

export default SiteAndGroupTree;